<template>
  <v-toolbar color="surface">
    <v-icon end icon="fas fa-square-root-alt" />
    <v-toolbar-title class="font-weight-medium">
      {{ $t('feedback-schemes.henry-coefficients.title') }}
    </v-toolbar-title>
    <v-spacer />
    <v-btn
      color="primary"
      icon="$add"
      size="small"
      :title="$t('feedback-schemes.henry-coefficients.create')"
      @click.stop="add"
    />
    <options-menu>
      <select-resource
        resource="feedback-schemes"
        return-object="henryCoefficients"
        @update:model-value="load"
      >
        <template #activator="{ props }">
          <v-list-item v-bind="props" link>
            <template #prepend>
              <v-icon icon="$download" />
            </template>
            <v-list-item-title>
              {{ $t('feedback-schemes.load') }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </select-resource>
      <json-editor-dialog v-model="items" @update:model-value="update" />
    </options-menu>
  </v-toolbar>
  <v-list class="list-border" lines="two">
    <vue-draggable
      v-model="items"
      :animation="300"
      handle=".drag-and-drop__handle"
      @end="update"
    >
      <v-list-item
        v-for="(coefficient, index) in items"
        :key="coefficient.id"
        :class="errors[index]?.length ? 'text-error' : undefined"
        :variant="errors[index]?.length ? 'tonal' : undefined"
      >
        <template #prepend>
          <v-drag-and-drop-handle />
          <v-icon>{{ getListItemIcon(coefficient) }}</v-icon>
        </template>
        <v-list-item-title class="font-weight-medium">
          {{ getListItemTitle(coefficient) }}
        </v-list-item-title>
        <template #append>
          <list-item-error :errors="errors[index]" />
          <v-list-item-action>
            <v-btn
              icon="$edit"
              :title="$t('feedback-schemes.henry-coefficients.edit')"
              @click.stop="edit(index, coefficient)"
            />
          </v-list-item-action>
          <v-list-item-action>
            <confirm-dialog
              color="error"
              icon
              icon-left="$delete"
              :label="$t('feedback-schemes.henry-coefficients.remove')"
              @confirm="remove(index)"
            >
              {{ $t('common.action.confirm.delete', { name: getListItemTitle(coefficient) }) }}
            </confirm-dialog>
          </v-list-item-action>
        </template>
      </v-list-item>
    </vue-draggable>
  </v-list>
  <v-dialog
    v-model="dialog.show"
    :fullscreen="$vuetify.display.smAndDown"
    max-width="600px"
    persistent
  >
    <v-card :tile="$vuetify.display.smAndDown">
      <v-toolbar>
        <v-icon end icon="fas fa-square-root-alt" />
        <v-toolbar-title>
          {{
            $t(`feedback-schemes.henry-coefficients.${dialog.index === -1 ? 'create' : 'edit'}`)
          }}
        </v-toolbar-title>
      </v-toolbar>
      <v-divider />
      <v-form ref="form" @submit.prevent="save">
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="dialog.item.sex"
                  hide-details="auto"
                  :items="sexes"
                  :label="$t('feedback-schemes.sexes._')"
                  name="sex"
                  variant="outlined"
                >
                  <template #item="{ item, props }">
                    <v-list-item v-bind="props" :title="item.title">
                      <template #prepend>
                        <v-icon :icon="item.icon" start />
                      </template>
                    </v-list-item>
                  </template>
                  <template #selection="{ item }">
                    <v-icon :icon="item.icon" start />
                    {{ item.title }}
                  </template>
                </v-select>
              </v-col>
            </v-row>
            <v-card-subtitle>
              {{ $t('feedback-schemes.age.range') }}
            </v-card-subtitle>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="dialog.item.age.start"
                  hide-details="auto"
                  :label="$t('feedback-schemes.age.start')"
                  name="age.start"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="dialog.item.age.end"
                  hide-details="auto"
                  :label="$t('feedback-schemes.age.end')"
                  name="age.end"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="dialog.item.weightCoefficient"
                  hide-details="auto"
                  :label="$t('feedback-schemes.henry-coefficients.weightCoefficient')"
                  name="weightCoefficient"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="dialog.item.heightCoefficient"
                  hide-details="auto"
                  :label="$t('feedback-schemes.henry-coefficients.heightCoefficient')"
                  name="heightCoefficient"
                  variant="outlined"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="dialog.item.constant"
                  hide-details="auto"
                  :label="$t('feedback-schemes.henry-coefficients.constant')"
                  name="constant"
                  variant="outlined"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn class="font-weight-bold" color="error" variant="text" @click.stop="reset">
            <v-icon icon="$cancel" start />{{ $t('common.action.cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn class="font-weight-bold" color="info" type="submit" variant="text">
            <v-icon icon="$success" start />{{ $t('common.action.ok') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';

import type { ReturnUseErrors } from '@intake24/admin/composables';
import type { HenryCoefficient, Sex } from '@intake24/common/feedback';

import { VueDraggable } from 'vue-draggable-plus';

import { OptionsMenu, SelectResource } from '@intake24/admin/components/dialogs';
import { JsonEditorDialog } from '@intake24/admin/components/editors';
import { ListItemError } from '@intake24/admin/components/lists';
import { useListWithDialog } from '@intake24/admin/composables';
import { sexes as sexesRef } from '@intake24/common/feedback';
import { ConfirmDialog, useI18n } from '@intake24/ui';

import { getHenryCoefficientDefaults } from './henry-coefficient';

defineOptions({ name: 'HenryCoefficientList' });

const props = defineProps({
  errors: {
    type: Object as PropType<ReturnUseErrors>,
    required: true,
  },
  modelValue: {
    type: Array as PropType<HenryCoefficient[]>,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);
const { i18n: { t } } = useI18n();

const { dialog, errors, form, items, add, edit, load, remove, reset, save, update }
  = useListWithDialog(
    props,
    { emit },
    { newItem: getHenryCoefficientDefaults, errorPrefix: 'henryCoefficients' },
  );

const sexes = sexesRef.map(value => ({
  title: t(`feedback-schemes.sexes.${value}`),
  value,
  icon: value === 'm' ? 'fas fa-mars' : 'fas fa-venus',
}));

function getGenderIcon(gender: Sex): string {
  const icons: Record<Sex, string> = { m: 'fas fa-mars', f: 'fas fa-venus' };
  return icons[gender];
}

function getListItemIcon(coefficient: HenryCoefficient): string {
  const { sex } = coefficient;

  return getGenderIcon(sex);
};

function getListItemTitle(coefficient: HenryCoefficient): string {
  const { sex, age } = coefficient;

  return `${t(`feedback-schemes.sexes.${sex}`)} (${age.start} => ${age.end})`;
};
</script>

<style lang="scss" scoped></style>
